import { Injectable, inject } from '@angular/core';
import { CollectionRequestBuilder, HttpErrorService } from '@web/common';
import {
  TableDataPayload,
  TableLazyLoad,
  ToastService,
  UploadException,
} from '@web/shared/ui';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  tap,
  throwError,
} from 'rxjs';
import { blank, executeOrNull, Guid } from '@web/utils';
import { AdminPaymentMethodCollectionItem } from '../models/admin-payment-method-collection-item';
import { AdminPaymentMethodCollectionFilter } from '../models/admin-payment-method-collection-filter';
import { AdminPaymentApiService } from '../api/services/admin-payment-api.service';
import {
  AdminMultimediaDataSourceService,
  AdminMultimediaImageUpload,
  multimediaFromResponse,
} from '@web/shared/module/multimedia';
import { AdminPaymentMethodDetails } from '../models/admin-payment-method-details';
import { AdminPaymentMethodUpdateRequest } from '../api/requests/admin-payment-method-update.request';
import { AdminPaymentMethodFormData } from '../models/admin-payment-method-form-data';
import { SummernoteImageUpload } from '@web/shared/external';

@Injectable({
  providedIn: 'root',
})
export class AdminPaymentMethodDataSourceService {
  private readonly adminPaymentApi = inject(AdminPaymentApiService);
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly toast = inject(ToastService);
  private readonly httpError = inject(HttpErrorService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminPaymentMethodCollectionFilter>,
  ): Observable<TableDataPayload<AdminPaymentMethodCollectionItem>> {
    /* prettier-ignore */
    return this.adminPaymentApi
      .fetchCollection(
        {
          request: this.collectionRequestBuilder
            .page(lazyLoad.page)
            .pageSize(lazyLoad.rows)
            .sorts((builder) =>
              builder.fromTableSort(lazyLoad.sort).buildAsArray(),
            )
            .filters((builder) =>
              builder
                .fromTableFilter(lazyLoad.filter)
                .appendDate((builder) => builder.rangeFor('createdAt').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('name').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('description').build())
                .buildAsArray(),
            )
            .build()
        },
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new AdminPaymentMethodCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    isActive: item.isActive,
                    previewFile: executeOrNull(item.previewFile, (item) => multimediaFromResponse(item)),
                    id: item.id,
                    name: item.name,
                    code: item.code,
                    description: item.description,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetailsById(
    id: Guid,
    skipByLoading = true,
  ): Observable<AdminPaymentMethodDetails> {
    return this.adminPaymentApi.fetchDetailsById({ id, skipByLoading }).pipe(
      map(
        (response) =>
          new AdminPaymentMethodDetails({
            canBeEdited: response.contextOperations.canBeEdited,
            canBeViewed: response.contextOperations.canBeViewed,
            createdAt: response.createdAt,
            code: response.code,
            description: response.description,
            id: response.id,
            name: response.name,
            isActive: response.isActive,
            previewFile: executeOrNull(response.previewFile, (item) =>
              multimediaFromResponse(item),
            ),
          }),
      ),
    );
  }

  public fetchDetailsFormById(
    id: Guid,
  ): Observable<AdminPaymentMethodFormData> {
    return this.adminPaymentApi
      .fetchDetailsById({ id, skipByLoading: true })
      .pipe(
        map(
          (response) =>
            new AdminPaymentMethodFormData({
              description: response.description,
              name: response.name,
              isActive: response.isActive,
              previewFile: executeOrNull(
                response.previewFile,
                (file) => new AdminMultimediaImageUpload({ id: file.id }),
              ),
            }),
        ),
      );
  }

  public updateByFormData(
    id: Guid,
    data: AdminPaymentMethodFormData,
  ): Observable<void> {
    return this.adminPaymentApi
      .update(
        id,
        new AdminPaymentMethodUpdateRequest({
          name: data.name,
          description: data.description,
          isActive: data.isActive,
          previewFileId: data.previewFile?.id,
        }),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess('Metoda płatności została zaktualizowana.')
              .build(),
          );
        }),
        blank(),
      );
  }

  public uploadEditorImages(
    files: File[],
  ): Observable<SummernoteImageUpload[]> {
    return combineLatest(
      files.map((file) =>
        this.multimediaDataSource
          .upload(file)
          .pipe(
            catchError((response) =>
              throwError(
                () =>
                  new UploadException(
                    this.httpError.extract(response).multiLineMessages(),
                  ),
              ),
            ),
          ),
      ),
    ).pipe(
      map((items) =>
        items.map(
          (item) => new SummernoteImageUpload(item.previewUrl, item.filename),
        ),
      ),
    );
  }
}
